import { useNavigate } from "react-router-dom";
import { Lock, AlertTriangle, Crown, Flame, Star, Shield, Gem, Zap, Circle, ArrowLeft, ArrowUpRight } from "lucide-react";
import useAccess from "../../hooks/useAccess";

const TIER_CONFIG = {
  free:     { label: "Free",           gradient: "from-gray-400 to-gray-500",       Icon: Circle  },
  wanderer: { label: "Wanderer",       gradient: "from-cyan-400 to-blue-500",        Icon: Zap     },
  kindred:  { label: "Kindred Spirit", gradient: "from-purple-400 to-purple-600",    Icon: Flame   },
  quester:  { label: "Quester",        gradient: "from-amber-400 to-orange-500",     Icon: Star    },
  pilgrim:  { label: "Pilgrim",        gradient: "from-emerald-400 to-teal-600",     Icon: Shield  },
  guardian: { label: "Guardian",       gradient: "from-rose-400 to-red-600",         Icon: Gem     },
  spire:    { label: "Spire",          gradient: "from-yellow-400 to-amber-600",     Icon: Crown   },
};

const UpgradePrompt = ({ reason = "tier", currentTier = "free", requiredTier = "wanderer", allowedTiers = [] }) => {
  const navigate = useNavigate();
  const { isLoggedIn } = useAccess();

  const config = TIER_CONFIG[requiredTier] || TIER_CONFIG.wanderer;
  const currentConfig = TIER_CONFIG[currentTier] || TIER_CONFIG.free;
  const { Icon: RequiredIcon } = config;

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center px-4 py-20">
      <div className="max-w-md w-full text-center">

        {/* Icon circle */}
        <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${config.gradient} flex items-center justify-center mx-auto mb-6 shadow-lg`}>
          {reason === "inactive" ? (
            <AlertTriangle size={36} className="text-white" strokeWidth={1.5} />
          ) : (
            <Lock size={36} className="text-white" strokeWidth={1.5} />
          )}
        </div>

        {/* Heading */}
        <h2 className="font-cinzel text-3xl font-bold text-gray-900 mb-3">
          {reason === "inactive" ? "Subscription Inactive" : "Membership Required"}
        </h2>

        {/* Message */}
        <p className="font-outfit text-gray-500 text-base mb-6 leading-relaxed">
          {reason === "inactive" ? (
            "Your subscription is currently inactive. Renew your plan to continue accessing this content."
          ) : (
            <>
              This content requires a{" "}
              <span className={`inline-flex items-center gap-1 font-semibold bg-gradient-to-r ${config.gradient} bg-clip-text text-transparent`}>
                <RequiredIcon size={14} className="inline" />
                {config.label}
              </span>{" "}
              membership or above.
              {currentTier !== "free" && (
                <> You are currently on the <strong>{currentConfig.label}</strong> plan.</>
              )}
            </>
          )}
        </p>

        {/* Allowed tier badges */}
        {reason === "tier" && allowedTiers.length > 0 && (
          <div className="flex flex-wrap justify-center gap-2 mb-6">
            {allowedTiers.map((t) => {
              const tc = TIER_CONFIG[t] || TIER_CONFIG.wanderer;
              const TIcon = tc.Icon;
              return (
                <span key={t}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold text-white bg-gradient-to-r ${tc.gradient}`}>
                  <TIcon size={13} strokeWidth={2.5} />
                  {tc.label}
                </span>
              );
            })}
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={() => navigate("/membership")}
            className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full bg-gradient-to-r from-[#B6FCFF] to-[#00C3FF] text-black font-semibold font-outfit hover:scale-105 transition-transform duration-300 shadow-md"
          >
            <ArrowUpRight size={16} strokeWidth={2.5} />
            {reason === "inactive" ? "Renew Membership" : "Upgrade Membership"}
          </button>
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full border border-gray-300 text-gray-600 font-outfit hover:bg-gray-100 transition-colors duration-300"
          >
            <ArrowLeft size={16} />
            Go Back
          </button>
        </div>

        {/* Login hint */}
        {!isLoggedIn && (
          <p className="mt-6 text-sm text-gray-400 font-outfit">
            Already a member?{" "}
            <button onClick={() => navigate("/login")} className="text-cyan-500 hover:underline font-semibold">
              Sign in
            </button>
          </p>
        )}
      </div>
    </div>
  );
};

export default UpgradePrompt;
